import React from 'react';
import { useDispatch } from 'react-redux';
import useAnalytics from 'helpers/useAnalytics';
import { toggleCategoryExpansion } from 'store/onboarding';

import makeStyles from '@material-ui/core/styles/makeStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Chip from '@material-ui/core/Chip';
import Collapse from '@material-ui/core/Collapse';
import Task from 'components/onboarding/Task';

import * as Types from 'components/onboarding/typings';

interface Props {
  category: Types.TaskCategory;
  isOnHomePage: boolean;
}

function Category({ category, isOnHomePage }: Props) {
  const classes = useStyles();
  const { id, isExpanded, title, description, completionPercentage, tasks } = category;
  const dispatch = useDispatch();
  const analytics = useAnalytics();

  const handleToggleCategoryOpen = (categoryId: string) => {
    dispatch(toggleCategoryExpansion(categoryId));
    analytics.track('Onboarding Category Expanded', {
      category: 'AD - Onboarding',
      label: title,
    });
  };

  const allTasksAreOptional = !tasks.some(task => task.required);

  return (
    <>
      <button onClick={() => handleToggleCategoryOpen(id)} className={classes.headerButton}>
        <div className={classes.title}>
          <Typography variant="body1">{title}</Typography>
          {completionPercentage === 1 && (
            <Chip label="Complete" size="small" className={classes.chip} />
          )}
        </div>
        <div className={classes.headerRight}>
          {!allTasksAreOptional && (
            <div className={classes.progress}>
              <Typography variant="body2" color="textSecondary">
                {`${Math.round(completionPercentage * 100)}%`}
                {isOnHomePage && ' Complete'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={completionPercentage * 100}
                className={classes.progressBar}
              />
            </div>
          )}
          {isExpanded ? (
            <ExpandMoreIcon data-testid="expanded-icon" />
          ) : (
            <ChevronRightIcon data-testid="to-expand-icon" />
          )}
        </div>
      </button>
      <Divider className={classes.divider} />
      {isExpanded && isOnHomePage && <Typography variant="body2">{description}</Typography>}
      <Collapse in={isExpanded}>
        <div>
          {tasks.map((task, i) => (
            <Task task={task} isOnHomePage={isOnHomePage} key={`task-${i}`} />
          ))}
        </div>
      </Collapse>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  headerButton: {
    width: '100%',
    background: 'none',
    border: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    cursor: 'pointer',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
  },
  headerRight: {
    display: 'flex',
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiLinearProgress-colorPrimary': {
      backgroundColor: 'rgba(97,166,14, 0.25)',
    },
  },
  progressBar: {
    width: 120,
    marginLeft: theme.spacing(),
    marginRight: theme.spacing(),
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.success.main,
    },
  },
  chip: {
    ...theme.typography.caption,
    marginLeft: theme.spacing(0.5),
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  divider: {
    width: '100%',
    marginLeft: 0,
  },
}));

export default Category;
